<template>
  <div v-if="loading" class="loading-placeholder">
    <loading-symbol />
  </div>
  <div class="provider-map" v-else-if="locations.length || mapMounted">
    <h2 v-if="this.currentRoute.id === 'detail'">{{ $t("provider.detail.location") }}</h2>
    <l-map ref="map" @ready="handleMapMount" class="provider-map"
        :zoom="defaultZoom" :center="center" :options="{ tap: false }">
      <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
      <l-marker @click="handleMarkerClick" :lat-lng="computeLocation(location)"
          v-for="location in locations" :icon="getIcon(location)" :key="location.locationId">
        <l-popup v-if="!isClusterLocation(location)" :options="{ autoPan: false }"
            :lat-lng="computeLocation(location)">
          <provider-popup :location="location" />
        </l-popup>
      </l-marker>
    </l-map>
  </div>
</template>

<style>
  @import '../../../node_modules/leaflet/dist/leaflet.css';

  .marker-wrapper {
    transform: rotate(-45deg);
    transform-origin: 50% 50%;
    border-radius: 50% 50% 50% 0;
    padding: 0 3px 3px 0;
    width: 38px;
    height: 38px;
    position: absolute;
    bottom: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    -webkit-box-shadow: -1px 1px 4px rgba(0, 0, 0, .5);
    -moz-box-shadow: -1px 1px 4px rgba(0, 0, 0, .5);
    box-shadow: -1px 1px 4px rgba(0, 0, 0, .5);
  }

  .marker-content {
    transform: rotate(45deg);
    margin-top: 4px;
    color: white;
    font-weight: bold;
    font-size: 14px;
  }

  .marker-wrapper img {
    width: 24px;
    height: 24px;
  }

  .provider-map {
    height: 450px;
    width: 100%;
    margin: 0 auto 58px auto;
    z-index: 0;
  }

  .loading-placeholder {
    height: 250px;
    padding-top: 200px;
  }

</style>

<script>
import L from 'leaflet';
import {
  LMap, LTileLayer, LMarker, LPopup,
} from 'vue2-leaflet';
import { mapState, mapActions } from 'vuex';
import LoadingSymbol from '@/components/LoadingSymbol.vue';
import ProviderPopup from './ProviderPopup.vue';

const COUNTRY_CLUSTER = 0;
const REGION_CLUSTER = 1;
const CITY_CLUSTER = 2;
const NORMAL = 3;

export default {
  name: 'provider-map',
  components: {
    LMap,
    LMarker,
    LTileLayer,
    ProviderPopup,
    LPopup,
    LoadingSymbol,
  },
  data() {
    return {
      center: L.latLng(37.3754338, -5.9900776),
      url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
      attribution: this.$t('provider.map.disclaimer'),
      // This attribute represents if the locations (not facets) were rendered.
      // And it's used to catch that user scrolled out of the locations
      // and facets should be loaded again.
      locationsRendered: false,
      locationParams: [
        {
          level: COUNTRY_CLUSTER, treshold: 2, precision: 4, refresh: false,
        },
        {
          level: REGION_CLUSTER, treshold: 7, precision: 7, refresh: true,
        },
        {
          level: CITY_CLUSTER, treshold: 9, precision: 11, refresh: true,
        },
        {
          level: NORMAL, treshold: 13, precision: null, refresh: true,
        },
      ],
      activeLevel: COUNTRY_CLUSTER,
      zoom: this.defaultZoom,
      mapMounted: false,
    };
  },
  props: {
    defaultZoom: {
      type: Number,
      default: () => 2,
    },
  },
  computed: {
    ...mapState({
      locations: (state) => state.location.list,
      filterParams: (state) => state.filter.form,
      currentCategory: (state) => state.category.active,
      currentRoute: (state) => state.router.route,
      categories: (state) => state.category.list,
      loading: (state) => state.location.list === undefined
          || state.category.list === undefined,
    }),
  },
  methods: {
    isClusterLocation(location) {
      return location.key !== undefined;
    },
    getNextLocationParams(currentLevel) {
      if (currentLevel + 1 < this.locationParams.length) {
        return this.locationParams[currentLevel + 1];
      }
      return this.locationParams[currentLevel];
    },
    getPreviousLocationParams(currentLevel) {
      if (currentLevel - 1 >= 0) {
        return this.locationParams[currentLevel - 1];
      }
      return this.locationParams[currentLevel];
    },
    refreshLocations(currentLevel) {
      if (this.locationParams[currentLevel].refresh) {
        this.setRefreshFlag();
      }
    },
    handleMarkerClick(e) {
      // Zoom only when map is clustered.
      if (this.activeLevel !== NORMAL) {
        this.$refs.map.mapObject.setView([e.latlng.lat, e.latlng.lng],
          this.getNextLocationParams(this.activeLevel).treshold);
      }
    },
    computeLocation(location) {
      // Location is facet - map clustering
      if (this.isClusterLocation(location)) {
        const coordinates = location.key.split(',');
        return L.latLng(coordinates[0], coordinates[1]);
      }
      return L.latLng(location.latitude, location.longitude);
    },
    getIcon(location) {
      // Location is normal - no map clustering
      if (!this.isClusterLocation(location)) {
        // Hack that allows to change attribute name on BE in the future.
        const locationBenefits = location.benefit ? location.benefit : location.benefits;
        const locationCategoryId = locationBenefits[0].primaryCategory.categoryId;
        // eslint-disable-next-line max-len
        const locationCategory = this.categories.find((category) => category.categoryId === locationCategoryId);
        const SVG_ICON_INDEX = 5;
        const iconUrl = locationCategory.icon[SVG_ICON_INDEX].url;
        const markerColor = locationCategory.color;
        return L.divIcon({
          className: 'location-map-marker',
          html: `<div class="marker-wrapper" style="background-color:${markerColor}">
              <div class="marker-content">
                <img src="${iconUrl}" />
              </div>
            </div>`,
          iconSize: [38, 38],
          iconAnchor: [19, 19],
        });
      }
      // Location is facet - map clustering
      return L.divIcon({
        className: 'location-map-marker',
        html: `<div><div class="marker-wrapper" style="background-color:#40b8b8">
              <div class="marker-content">${location.count !== 1 ? location.count : ''}</div>
            </div>`,
        iconSize: [38, 38],
        iconAnchor: [19, 19],
      });
    },
    registerMapListener() {
      // Map listener for zoom and move on map
      this.$refs.map.mapObject.on('moveend', (event) => {
        if (event.target.getZoom() > this.zoom) {
          // User is scrolling in
          const nextParams = this.getNextLocationParams(this.activeLevel);
          if (event.target.getZoom() >= nextParams.treshold) {
            this.activeLevel = nextParams.level;
          }
        } else if (event.target.getZoom() < this.zoom) {
          // User is scrolling out
          const previousParams = this.getPreviousLocationParams(this.activeLevel);
          if (event.target.getZoom() >= previousParams.treshold
              && event.target.getZoom() < this.locationParams[this.activeLevel].treshold) {
            // We don't want to redo request for COUNTRY cluster
            // but when zooming out we need to refresh it so COUNTRY clusters show.
            if (this.activeLevel === REGION_CLUSTER && previousParams.level === COUNTRY_CLUSTER) {
              this.setRefreshFlag();
            }
            this.activeLevel = previousParams.level;
          }
        }
        // If location fetch need to be called again it will.
        this.refreshLocations(this.activeLevel);
        this.zoom = this.$refs.map.mapObject.getZoom();
        this.fetchLocations(this.createFetchParams());
      });
    },
    createMapBoundString(mapBoundings) {
      // eslint-disable-next-line no-underscore-dangle
      return `${mapBoundings.getNorthWest().lat},${mapBoundings.getNorthWest().lng}:${mapBoundings.getSouthEast().lat},${mapBoundings.getSouthEast().lng}`;
    },
    handleMapMount() {
      this.mapMounted = true;
      // Set zoom and center so all markers are in the map. Do it only for detail
      // On main map it doesn't look good.
      if (this.currentRoute.id === 'detail') {
        const markers = [];
        this.$refs.map.mapObject.eachLayer((layer) => {
          if (layer.options.pane === 'markerPane') {
            markers.push(layer);
          }
        });
        if (markers.length !== 0) {
          const markerGroup = new L.FeatureGroup(markers);
          this.$refs.map.mapObject.fitBounds(markerGroup.getBounds());
        }
        if (markers.length === 1) {
          this.$refs.map.mapObject.openPopup(markers[0].getPopup());
        }
      }
      // Register map event watcher
      this.registerMapListener();
    },
    ...mapActions({
      fetchLocations: 'fetchLocations',
      resetLocations: 'resetLocations',
      setRefreshFlag: 'setRefreshFlag',
    }),
    createFetchParams() {
      if (this.currentRoute.id === 'detail') {
        // We want all locations of provider to be shown.
        // That's why clustering and map bounding is not on detail.
        return {
          providerIds: this.currentRoute.params.providerId,
        };
      }
      // Compute params for map page.
      let requestParams = {
        ...this.filterParams,
        category: this.currentCategory,
      };
      if (this.activeLevel === NORMAL) {
        return Object.assign(requestParams, {
          boundingBox: this.createMapBoundString(this.$refs.map.mapObject.getBounds()),
        });
      }
      requestParams = Object.assign(requestParams, {
        facets: 'gpsCoordinates',
        precision: this.locationParams[this.activeLevel].precision,
      });
      if (this.activeLevel !== COUNTRY_CLUSTER && this.$refs.map) {
        requestParams.boundingBox = this.createMapBoundString(this.$refs.map.mapObject.getBounds());
      }
      return requestParams;
    },
  },
  created() {
    this.fetchLocations(this.createFetchParams());
  },
  updated() {
    this.fetchLocations(this.createFetchParams());
  },
  beforeDestroy() {
    // This is here when the filter params are changed.
    // So we want to show the loading again and fetch new locations.
    this.resetLocations();
  },
};
</script>
